<template>
  <Dialog
    class="recurrence-break-modal"
    :visible="visible"
    :modal="true"
    :header="header"
    :draggable="false"
    @update:visible="$emit('update:visible', $event)"
    @hide="onHide"
  >
    <p class="recurrence-break-modal__prompt">{{ prompt }}</p>
    <div class="recurrence-break-modal__options">
      <Button
        v-for="option in options"
        :key="option.scope"
        :label="option.label"
        class="p-button-outlined recurrence-break-modal__option"
        @click="choose(option.scope)"
      />
    </div>
    <template #footer>
      <Button label="Cancel" class="p-button-text" @click="cancel" />
    </template>
  </Dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { RecurrenceBreakScope } from '@/models/RecurrenceBreakScope'

interface RecurrenceBreakOption {
  scope: RecurrenceBreakScope
  label: string
}

export default Vue.extend({
  name: 'RecurrenceBreakModal',
  components: { Dialog, Button },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    // 'calendar' (drag/drop) omits the "All" option; 'tacticDetail' includes it.
    context: {
      type: String as PropType<'calendar' | 'tacticDetail'>,
      required: true,
    },
    // 'edit' vs 'delete' only changes the header/prompt wording, not the option set.
    mode: {
      type: String as PropType<'edit' | 'delete'>,
      default: 'edit',
    },
  },
  data() {
    return {
      // Guards against onHide emitting a second 'cancel' after an explicit choice/cancel.
      resolved: false as boolean,
    }
  },
  computed: {
    header(): string {
      return this.mode === 'delete' ? 'Delete Recurring Tactic' : 'Edit Recurring Tactic'
    },
    prompt(): string {
      return this.mode === 'delete'
        ? 'This is a recurring tactic. What would you like to delete?'
        : 'This is a recurring tactic. How would you like to apply this change?'
    },
    options(): RecurrenceBreakOption[] {
      const thisInstance: RecurrenceBreakOption = {
        scope: RecurrenceBreakScope.ThisInstance,
        label: 'This Tactic',
      }
      const thisAndFollowing: RecurrenceBreakOption = {
        scope: RecurrenceBreakScope.ThisAndFollowing,
        label: 'This and following Tactics',
      }
      const allInstances: RecurrenceBreakOption = {
        scope: RecurrenceBreakScope.AllInstances,
        label: 'All Tactics in the series',
      }

      return this.context === 'tacticDetail'
        ? [allInstances, thisInstance, thisAndFollowing]
        : [thisInstance, thisAndFollowing]
    },
  },
  methods: {
    choose(scope: RecurrenceBreakScope): void {
      this.resolved = true
      this.$emit('select', scope)
      this.$emit('update:visible', false)
    },
    cancel(): void {
      this.resolved = true
      this.$emit('cancel')
      this.$emit('update:visible', false)
    },
    onHide(): void {
      // Dialog dismissed via the X/escape without choosing -> treat as cancel.
      if (!this.resolved) {
        this.$emit('cancel')
      }
      this.resolved = false
    },
  },
})
</script>

<style lang="scss">
.recurrence-break-modal {
  .recurrence-break-modal__prompt {
    margin: 0 0 1.25rem;
  }
  .recurrence-break-modal__options {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .recurrence-break-modal__option {
    width: 100%;
    justify-content: center;
  }
}

// --- Show/dismiss motion -----------------------------------------------------
// The `recurrence-break-modal` class is applied to both the mask (backdrop) and the
// dialog panel. Backdrop: fade the overlay colour only. Panel: fade + scale 10% from/to
// centre. Neither may translate — `transform` is pinned to scale-only so no PrimeVue
// (or positioned-dialog) rule can shift it sideways.

.p-dialog-mask.recurrence-break-modal {
  transform: none;

  &.p-component-overlay-enter {
    animation: recurrence-break-modal-overlay-enter 150ms forwards;
  }
  &.p-component-overlay-leave {
    animation: recurrence-break-modal-overlay-leave 150ms forwards;
  }
}

.p-dialog.recurrence-break-modal {
  // Width set here (not via :style on <Dialog>, which lands on the mask and collapses the
  // backdrop to a left-anchored strip on dismiss).
  width: 420px;
  max-width: 90vw;
  transform-origin: center center;

  &.p-dialog-enter-active,
  &.p-dialog-leave-active {
    transition: opacity 150ms ease, transform 150ms ease;
  }
  &.p-dialog-enter,
  &.p-dialog-leave-to {
    opacity: 0;
    transform: scale(0.9);
  }
  &.p-dialog-enter-to,
  &.p-dialog-leave {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes recurrence-break-modal-overlay-enter {
  from {
    background-color: transparent;
  }
  to {
    background-color: rgba(0, 0, 0, 0.4); // matches $maskBg
  }
}
@keyframes recurrence-break-modal-overlay-leave {
  from {
    background-color: rgba(0, 0, 0, 0.4);
  }
  to {
    background-color: transparent;
  }
}
</style>
